import {useQueryClient} from '@github-ui/react-query'
import {useLayoutQueryKey} from '../page-data/loaders/use-layout-query'
import type {HeaderPageData} from '../page-data/payloads/header'
import type {PullRequestState} from '../types'
import {produce} from 'immer'
import type {MainQueryResult} from '@github-ui/react-core/future/main-query'
import {useOverviewRouteQueryKey} from '../components/overview/data/use-overview-route-data'

/** Returns a function that can be used to update the pull request state in the layout route query. */
function useSetPrStateQueryData() {
  const queryClient = useQueryClient()

  const layoutQueryKey = useLayoutQueryKey()

  return (newState: PullRequestState) => {
    const currentState = queryClient.getQueryData(layoutQueryKey)?.payload.pullRequest.state

    if (!currentState) return

    const applyUpdatedState = produce((draft?: MainQueryResult<HeaderPageData>) => {
      if (draft) draft.payload.pullRequest.state = newState
    })

    queryClient.setQueryData(layoutQueryKey, applyUpdatedState)

    return currentState
  }
}

/** Invalidate queries tied to the current PR state (open/closed/draft), triggering refetches.
 * We also optionally optimistically update the state in addition to invalidating it.
 */
export function useInvalidatePrState() {
  const queryClient = useQueryClient()
  const layoutQueryKey = useLayoutQueryKey()
  const overviewQueryKey = useOverviewRouteQueryKey()

  const updateState = useSetPrStateQueryData()

  return async (optimisticState?: PullRequestState) => {
    if (optimisticState) updateState(optimisticState)

    await Promise.all([
      queryClient.invalidateQueries({queryKey: layoutQueryKey, exact: true}),
      // Overview route owns `sinceSelectors` used by the activity sidebar section
      overviewQueryKey && queryClient.invalidateQueries({queryKey: overviewQueryKey, exact: true}),
    ])
  }
}

/** Callbacks for a state change mutation that show the new PR state immediately and roll it back if the request fails. */
export function useOptimisticPrStateCallbacks(newState: PullRequestState) {
  const updateState = useSetPrStateQueryData()
  const invalidateState = useInvalidatePrState()

  return {
    onMutate: () => {
      const previousState = updateState(newState)
      return previousState ? () => void updateState(previousState) : undefined
    },
    onSuccess: () => invalidateState(newState),
  }
}
